import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Accordion,
  Alert,
  Placeholder,
  Button,
  ListGroup,
} from "react-bootstrap";
import { fetchBeneficios } from "../redux/beneficiosSlice";
import { resolverUrlArchivo } from "../utils/resolverUrlArchivo";
import "../styles/DocumentosPage.css"; // Importamos los mismos estilos
import { markdownLigeroAHtml } from "../utils/richText";
import useEnlaceExterno from "../utils/useEnlaceExterno";

const BeneficiosPage = () => {
  const dispatch = useDispatch();
  const { beneficios, loading, error } = useSelector(
    (state) => state.beneficios,
  );
  const onClickEnlace = useEnlaceExterno();

  useEffect(() => {
    dispatch(fetchBeneficios());
  }, [dispatch]);

  return (
    <div
      className="documentos-container"
      role="main"
      aria-label="Página de beneficios para socios"
    >
      {/* Header con logo y título */}
      <div className="documentos-header" aria-label="Encabezado de beneficios">
        <img
          src="https://api.sinterec.cl/public/images/SINTEREC_logo.jpg"
          alt="Logo SINTEREC"
          className="logo-sinterec"
          aria-hidden="true"
        />
        <h1 className="documentos-title" aria-level="1">
          Nuestros Beneficios
        </h1>
      </div>

      {error && (
        <Alert variant="danger" dismissible role="alert" aria-live="assertive">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {loading ? (
        <Accordion
          className="documentos-accordion"
          aria-label="Cargando beneficios..."
        >
          {[1, 2, 3].map((index) => (
            <Accordion.Item
              eventKey={index.toString()}
              key={index}
              className="documento-item"
              aria-hidden="true"
            >
              <Accordion.Header className="documento-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="d-flex align-items-center">
                    <i
                      className="bi bi-gift-fill documento-icon me-3 text-primary"
                      aria-hidden="true"
                    ></i>
                    <span className="documento-title">
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={6} />
                      </Placeholder>
                    </span>
                  </div>
                  <div className="text-muted documento-date">
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={4} />
                    </Placeholder>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="documento-body">
                <Placeholder as="p" animation="glow">
                  <Placeholder xs={12} />
                  <Placeholder xs={8} />
                </Placeholder>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      ) : (
        <>
          {beneficios.length > 0 ? (
            <Accordion
              className="documentos-accordion"
              role="region"
              aria-label="Lista de beneficios disponibles"
            >
              {beneficios.map((beneficio, index) => {
                // urlFinal (virtual del backend) resuelve el documento enlazado.
                // Fallback a beneficio.url por compatibilidad.
                const urlDocumento = resolverUrlArchivo(
                  beneficio.urlFinal || beneficio.url,
                );

                return (
                  <Accordion.Item
                    eventKey={index.toString()}
                    key={beneficio.id}
                    className="documento-item"
                    aria-labelledby={`beneficio-heading-${index}`}
                  >
                    <Accordion.Header
                      className="documento-header"
                      id={`beneficio-heading-${index}`}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                          <i
                            className="bi bi-gift-fill documento-icon me-3 text-primary"
                            aria-hidden="true"
                          ></i>
                          <span className="documento-title">
                            {beneficio.nombre}
                          </span>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body
                      className="documento-body"
                      id={`beneficio-content-${index}`}
                      aria-labelledby={`beneficio-heading-${index}`}
                    >
                      <ListGroup variant="flush">
                        <ListGroup.Item
                          className="border-0 px-0 py-1"
                          aria-label={`Descripción: ${beneficio.descripcion}`}
                        >
                          <strong>Descripción:</strong>
                          <div
                            onClick={onClickEnlace}
                            style={{ whiteSpace: "pre-wrap" }}
                            dangerouslySetInnerHTML={{
                              __html: markdownLigeroAHtml(
                                beneficio.descripcion,
                              ),
                            }}
                          />
                        </ListGroup.Item>
                      </ListGroup>
                      {urlDocumento && (
                        <div className="d-flex gap-2 mt-3">
                          <Button
                            variant="outline-primary"
                            href={urlDocumento}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="documento-btn"
                            aria-label={`Ver documento de ${beneficio.nombre} (se abre en nueva ventana)`}
                          >
                            <i
                              className="bi bi-file-earmark-text me-1"
                              aria-hidden="true"
                            ></i>
                            Ver documento
                          </Button>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <Alert
              variant="info"
              className="mt-4"
              role="status"
              aria-live="polite"
            >
              No hay beneficios disponibles en este momento.
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export default BeneficiosPage;
